import type { Entity } from '../components'
import type { GameWorld } from '../world'
import type { EventBus } from '../events'
import { applyPlayerRadius } from '../growth'
import { addSpurtProgress } from '../growth-spurt'
import { morphologyFrom } from '../creature-recipe'
import { T } from '../tunables'

export function spawnDnaOrb(world: GameWorld, x: number, y: number, amount: number, ownOrb = false, size = 0): void {
  world.add({
    position: { x, y },
    velocity: { x: 0, y: 0 },
    wobble: { phase: Math.random() * Math.PI * 2, freq: 1.2, amp: 0.18 },
    renderable: {
      shape: 'orb',
      // Dropped mass makes the orb visibly bigger — "that's my body over there".
      radius: 0.12 + size * 0.08,
      tint: [1.0, 0.85, 0.2],
    },
    // FEAT_GEM: pickups are the slide's only geometric shapes.
    morphology: morphologyFrom({ body: 'gem' }, Math.random()),
    dnaOrb: { amount, ownOrb, size },
  })
}

export function dnaOrbSystem(world: GameWorld, player: Entity, events: EventBus, dt = 0): void {
  const playerR = player.renderable?.radius ?? 0.12
  // Static cling (Phase 6c) magnetizes DNA orbs in addition to mutation orbs.
  const magnetRange = player.mods?.orbMagnetRange ?? 0
  for (const e of world.with('dnaOrb', 'position', 'renderable')) {
    if (magnetRange > 0 && dt > 0) {
      const mdx = player.position.x - e.position.x
      const mdy = player.position.y - e.position.y
      const md = Math.hypot(mdx, mdy)
      const totalRange = magnetRange + e.renderable.radius + playerR
      if (md > 0.0001 && md < totalRange) {
        const pull = 1 - md / totalRange
        const step = T.orbs.dnaMagnetPullSpeed * pull * dt
        e.position.x += (mdx / md) * step
        e.position.y += (mdy / md) * step
      }
    }

    const dx = e.position.x - player.position.x
    const dy = e.position.y - player.position.y
    const dist2 = dx * dx + dy * dy

    // first_death_orb: proximity milestone before actual pickup
    if (e.dnaOrb.ownOrb && dist2 < T.orbs.firstDeathOrbRadius * T.orbs.firstDeathOrbRadius) {
      events.emit({ type: 'orb.deathOrbNearby' })
    }

    const contactDist = e.renderable.radius + playerR
    if (dist2 < contactDist * contactDist) {
      player.dna = (player.dna ?? 0) + e.dnaOrb.amount
      // Own-orb retrieval is reclaimed income, not new — no spurt credit.
      if (!e.dnaOrb.ownOrb) addSpurtProgress(player, e.dnaOrb.amount)
      // Retrieval restores the eating-growth that died with you (max, not
      // additive — you get your body back, you don't stack two bodies).
      if (e.dnaOrb.size) {
        player.sizeFromEating = Math.min(
          T.growth.maxSizeFromEating,
          Math.max(player.sizeFromEating ?? 0, e.dnaOrb.size),
        )
        applyPlayerRadius(player)
      }
      world.remove(e)
      events.emit({ type: 'orb.collected', kind: 'dna', value: e.dnaOrb.amount })
      break  // at most one orb
    }
  }
}
